import dayjs from 'dayjs';
import * as yup from 'yup';
import { ScopeableType } from '../../../types';
import { checkVbdOverlap, IDiscount } from '../../../utils/discount-utils';
import { ScopeItem } from '../../form-fields/scope-selector/scope-selector-constants';

export const VbdValidationSchema = yup.object({
  level: yup
    .string()
    .oneOf(['partnership', 'client'] as const)
    .required(),
  partnershipId: yup
    .number()
    .nullable()
    .when('level', ([level], schema) => {
      if (level !== 'client') {
        return schema.required('Partnership is required');
      }
      return schema;
    }),
  clientId: yup
    .number()
    .nullable()
    .when('level', ([level], schema) => {
      if (level === 'client') {
        return schema.required('Client is required');
      }
      return schema;
    }),
  scopeableType: yup.string().oneOf(Object.values(ScopeableType)).required(),
  scopes: yup
    .array()
    .of(
      yup.object({
        scopeableType: yup
          .string()
          .oneOf(Object.values(ScopeableType))
          .required(),
        scopeableId: yup.number().nullable().required('Selection required'),
      }),
    )
    .when('scopeableType', ([type], schema) => {
      if (type === ScopeableType.Global) {
        return schema.max(0, 'Global scope cannot include selections');
      }
      return schema
        .min(1, 'Select at least one')
        .test(
          'same-type',
          'All selections must share the same scope type',
          (arr: ScopeItem[] | undefined) =>
            !arr || arr.every(s => s.scopeableType === type),
        );
    }),
  startDate: yup
    .string()
    .nullable()
    .required('Start date is required')
    .test('not-in-past', 'Start date cannot be in the past', function (value) {
      const { editDefinitionId } = (this?.options?.context ?? {}) as {
        editDefinitionId?: number;
      };
      if (editDefinitionId != null || !value) return true;
      return !dayjs(value).isBefore(dayjs(), 'day');
    })
    .when(
      ['partnershipId', 'clientId'],
      ([partnershipId, clientId], schema) => {
        if (partnershipId != null || clientId != null) {
          return schema.test({
            name: 'startDateOverlaps',
            skipAbsent: true,
            test(value, ctx) {
              const { vbds, editDefinitionId } = (ctx?.options?.context ??
                {}) as {
                vbds?: IDiscount[];
                editDefinitionId?: number;
              };
              const { endDate, partnershipId: pid, clientId: cid } = ctx.parent;
              if (!vbds || !value) return true;
              const { startDateOverlaps } = checkVbdOverlap(
                value,
                endDate ?? null,
                pid ?? null,
                cid ?? null,
                vbds,
                editDefinitionId,
              );
              if (startDateOverlaps) {
                return ctx.createError({
                  message: 'Start date overlaps an existing VBD',
                });
              }
              return true;
            },
          });
        }
        return schema;
      },
    ),
  endDate: yup
    .string()
    .nullable()
    .defined()
    .test(
      'after-start',
      'End date must be after start date',
      function (endDate) {
        const { startDate } = this.parent;
        if (!endDate || !startDate) return true;
        return dayjs(endDate).isAfter(dayjs(startDate));
      },
    )
    .when(
      ['partnershipId', 'clientId'],
      ([partnershipId, clientId], schema) => {
        if (partnershipId != null || clientId != null) {
          return schema.test({
            name: 'endDateOverlaps',
            skipAbsent: true,
            test(value, ctx) {
              const { vbds, editDefinitionId } = (ctx?.options?.context ??
                {}) as {
                vbds?: IDiscount[];
                editDefinitionId?: number;
              };
              const {
                startDate,
                partnershipId: pid,
                clientId: cid,
              } = ctx.parent;
              if (!vbds || !startDate) return true;
              const { endDateOverlaps } = checkVbdOverlap(
                startDate,
                value ?? null,
                pid ?? null,
                cid ?? null,
                vbds,
                editDefinitionId,
              );
              if (endDateOverlaps) {
                return ctx.createError({
                  message: 'End date overlaps an existing VBD',
                });
              }
              return true;
            },
          });
        }
        return schema;
      },
    ),
  suppressEmptyCharges: yup.boolean().defined(),
  tiers: yup
    .array()
    .of(
      yup.object({
        upperBound: yup.number().nullable().defined(),
        discount: yup
          .number()
          .nullable()
          .required('Discount is required')
          .min(0, 'Must be 0 or greater'),
        c1: yup
          .number()
          .nullable()
          .required('C1 is required')
          .min(0, 'Must be 0–100')
          .max(100, 'Must be 0–100'),
        c2: yup
          .number()
          .nullable()
          .required('C2 is required')
          .min(0, 'Must be 0–100')
          .max(100, 'Must be 0–100'),
        c3: yup
          .number()
          .nullable()
          .required('C3 is required')
          .min(0, 'Must be 0–100')
          .max(100, 'Must be 0–100'),
      }),
    )
    .required()
    .min(1)
    .test('upper-bounds', 'Tier upper bounds are invalid', function (tiers) {
      if (!tiers) return true;
      for (let i = 0; i < tiers.length - 1; i++) {
        const ub = tiers[i].upperBound;
        if (ub == null || !Number.isInteger(ub) || ub < 1) {
          return this.createError({
            path: `${this.path}[${i}].upperBound`,
            message: 'Enter the max subscriptions for this tier',
          });
        }
        if (i > 0 && ub <= (tiers[i - 1].upperBound ?? 0)) {
          return this.createError({
            path: `${this.path}[${i}].upperBound`,
            message: 'Must be greater than the previous tier',
          });
        }
      }
      return true;
    })
    .test('c-sum', 'C1 + C2 + C3 must equal 100%', function (tiers) {
      if (!tiers) return true;
      for (let i = 0; i < tiers.length; i++) {
        const { c1, c2, c3 } = tiers[i];
        if (c1 == null || c2 == null || c3 == null) continue;
        if (Math.round(c1 + c2 + c3) !== 100) {
          return this.createError({
            message: 'C1 + C2 + C3 must equal 100%',
          });
        }
      }
      return true;
    }),
});
